import { Box, Button, Grid } from '@mui/material';
import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import ResourceAPI from 'api/ResourceAPI';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps, useHistory, useParams, useLocation, useRouteMatch } from 'react-router-dom';
import useApi from 'hooks/useApi';
import { useSelector } from 'store/hooks';
import Order from 'types/entities/Order';
import User from 'types/entities/User';
import { getPurchaseOrderAction } from 'store/actions/purchaseOrderActions';
import {
  getbaseLineOrderAction,
  getbaseLineSourceOrderAction,
  loadDeliveryManagersAction,
} from 'store/actions/changeBaselineActions';
import {
  injectOrderAction,
  injectScopesAction,
  injectSelectedWorkunitsAction,
} from 'store/actions/catalogToOrderActions';
import OrderApiService from 'api/OrderApiService';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Layout, StepperHeader } from 'components';
import EngagementParameters from './ChangeBaselinePages/EngagementParameters/EngagementParameters';
import NewBaseline from './ChangeBaselinePages/NewBaseline/NewBaseline';
import BaselineAffectation from './ChangeBaselinePages/BaselineAffectation/BaselineAffectation';
import BaselineOrderSummary from './ChangeBaselinePages/BaselineOrderSummary/BaselineOrderSummary';
import { useAppState } from 'store/Provider';
import BaselineOrderConfirmation from './ChangeBaselineDialogs/BaselineConfirmation/BaselineOrderConfirmation';
import { openDialogAction } from 'store/actions/dialogsActions';

import { formatDateToddMMYYY } from 'helpers/format';
import { setSnackbarAction } from 'store/actions/snackbarActions';
import NewBaseLineOrderWorkunits from './ChangeBaselinePages/NewBaseline/NewBaseLineOrderWorkunits';
// import EditTotalWorkunit from './ChangeBaselinePages/NewBaseline/EditTotalWorkunit/EditTotalWorkunit';
import Scope from 'types/entities/Scope';
import BaselineTotalWorkunit from './ChangeBaselineDialogs/BaselineTotalWorkunit';
import BaselineEditWorkunit from './ChangeBaselineDialogs/BaselineEditWorkunit';
import UserApiService from 'api/UserApiService';
import DeliverableBLDialogDelete from './ChangeBaselineDialogs/DeliverableBLDialogDelete';
import useManagementService from 'features/Management/services/useManagementService';
import BaselineEditWorkunitCancelConfirmation from './ChangeBaselineDialogs/BaselineConfirmation/BaselineEditWorkunitCancelConfirmation';
import ConfirmationDialogQuickAction from './ChangeBaselineDialogs/ConfirmationDialogQuickAction/ConfirmationDialogQuickAction';
import moment from 'moment';

interface RouteParams {
  order_id: string;
}

export interface ChildRefType {
  handleSubmitEngagementPara: (fname: string) => void;
}

export interface ChildRefbaseType {
  handleSubmitNewbaseLine: (fname: string) => void;
}

export interface ChildRefBLLastpageType {
  handleSubmitBLSummary: (fname: string) => void;
}

interface CustomStep {
  id: number;
  title: string;
  subtitle: string;
}

const ChangeBaseline: React.FC<RouteComponentProps<RouteParams>> = () => {
  const { order_id } = useParams<RouteParams>();
  const location = useLocation();
  const match = useRouteMatch<RouteParams>();
  const { makeCall } = useApi();
  const { dispatch } = useAppState();
  const [t] = useTranslation();
  const history = useHistory();
  const { baselineCancelDialogApi } = useManagementService();
  const EngagementParametersRef = useRef<ChildRefType>(null);
  const NewBaselineRef = useRef<ChildRefbaseType>(null);
  const appState = useSelector((state) => state.app);
  const sourceOrder = useSelector((state) => state.changeBaseline?.sourceOrder);
  const baseLineOrder = useSelector((state) => state.changeBaseline);
  const dialogData = useSelector((state) => state.dialogs);

  // State
  const [activeBaseFlag, setActiveBaseFlag] = useState('EngagementPage');
  const [activeStep, setActiveStep] = useState(0);
  const [completed, setCompleted] = useState<{ [k: number]: boolean }>({});
  const [sourceBaseOrder, setSourceBaseOrder] = useState(sourceOrder);
  const [baselineOrderData, setBaselineOrderData] = useState<any>(baseLineOrder);
  const [selectedConsultants, setSelectedConsultants] = useState<User[]>([]);
  const [deliveryManagers, setDeliveryManagers] = useState<User[]>([]);
  const [representatives, setRepresentatives] = useState<User[]>([]);
  const [selectedDeliveryManager, setSelectedDeliveryManager] = useState<User[]>([]);
  const [selectedRepresentative, setSelectedRepresentative] = useState<User[]>([]);
  const [selectedScopes, setSelectedScopes] = useState<any[]>([]);
  const [Scopes, setScopes] = useState<Scope[]>([]);
  const [availableScopes, setAvailableScopes] = useState<Scope[]>([]);
  const [availableConsultants, setAvailableConsultants] = useState<User[]>([]);
  const [availableDeliveryManagers, setAvailableDeliveryManagers] = useState<User[]>([]);
  const [availableRepresentatives, setAvailableRepresentatives] = useState<User[]>([]);
  const [currentStep, setCurrentStep] = useState(1);
  const [isMissingInputs, setIsMissingInputs] = useState(false); // Validation

  // Prevent multiple API calls
  const [isCreating, setIsCreating] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const isMountedRef = useRef(true);

  const totalSteps = 4;

  // Step components
  const steps: CustomStep[] = [
    { id: 1, title: t('engagement_parameters'), subtitle: t('engagement_subtitle') },
    { id: 2, title: t('baselinepagename'), subtitle: t('new_baseline_subtitle') },
    { id: 3, title: t('affectation'), subtitle: t('affectation_subtitle') },
    { id: 4, title: t('order_summary'), subtitle: t('order_summary_subtitle') },
  ];

  // Memoized functions to prevent re-renders
  const getPurchaseOrder = useCallback(
    async (order: Order) => {
      if (!order?.id || !order?.affair?.otp) return;
      // setIsLoading(true);
      try {
        dispatch(getPurchaseOrderAction([]));
        const purchaseOrderList = await makeCall(
          OrderApiService.fetchEditPurchaseOrder(order.id, order.affair.otp as string)
        );

        if (purchaseOrderList.length > 0) {
          purchaseOrderList.forEach(
            (po: { poend: string; poref: string; reference: string; postart: string; is_valid: boolean }) => {
              po.reference = `${po.poref} (${formatDateToddMMYYY(po.postart)} - ${formatDateToddMMYYY(po.poend)})`;
            }
          );
        }

        if (isMountedRef.current) {
          dispatch(getPurchaseOrderAction(purchaseOrderList));
        }
        // setIsLoading(false);
      } catch (error) {
        console.error('Failed to fetch purchase orders:', error);
        // setIsLoading(false);
      }
    },
    [makeCall, dispatch]
  );

  const getPoScopeByBaseline = async (currentOrder_otp: any, id: number) => {
    const { purchase_orders, order_scopes } = await OrderApiService.fetchPoScopeBaseline(
      Number(id),
      currentStep,
      currentOrder_otp
    );

    setScopes(order_scopes?.datas);
    dispatch(injectScopesAction(order_scopes?.datas));
    dispatch(getPurchaseOrderAction(purchase_orders));
  };

  const savedFetchOrder = useCallback(
    async (fnName: boolean, baseline_version: number) => {
      // if (isLoading || !baselineOrderData?.id) return;
      dispatch(getbaseLineOrderAction(null));
      // setIsLoading(true);
      try {
        const { orderForCustomer, totalPage, currentPage } = await makeCall(
          OrderApiService.fetchBaselineOrdersListManagement(appState?.customer?.id as number, {
            id: baselineOrderData?.id ? baselineOrderData?.id : Number(order_id),
            size: 1,
            customer_id: 1,
            page_typeid: currentStep,
            ...(baseline_version !== 0 && { baseline_version: baseline_version + 1 }),
          })
        );

        if (orderForCustomer?.[0] && isMountedRef.current) {
          const order = orderForCustomer[0];
          dispatch(getbaseLineOrderAction(order));
          dispatch(injectOrderAction(order));
          if (fnName) {
            getPoScopeByBaseline(order?.affair_otp, order?.id);
          }
          setSelectedScopes(order.tmscopes || []);
          setSelectedConsultants(order.tmconsultants || []);
          setSelectedDeliveryManager(order.tmaccountables || []);
          setSelectedRepresentative(order.tmdelegues || []);
          setAvailableScopes(order.scopes || []);
          setAvailableConsultants(order.consultants || []);
          setAvailableDeliveryManagers(order.delivery_managers || []);
          setAvailableRepresentatives(order.representatives || []);
        }
      } catch (error) {
        console.error('Failed to fetch saved order:', error);
      } finally {
        setIsLoading(false);
      }
    },
    [makeCall, appState?.customer?.id, order_id, dispatch, getPurchaseOrder]
  );

  useEffect(() => {
    const getDeliveryManagers = async () => {
      try {
        const res = await UserApiService.fetchPossiblesDeliverableTeamLeaders();

        const sortedRes = res.sort((a, b) => {
          const nameA = a.last_name.toUpperCase();
          const nameB = b.last_name.toUpperCase();
          if (nameA < nameB) return -1;
          if (nameA > nameB) return 1;
          return 0;
        });

        dispatch(loadDeliveryManagersAction(sortedRes));
      } catch (error) {
        console.error('Failed to fetch saved order:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (appState.customer?.id) getDeliveryManagers();
  }, [appState.customer?.id]);

  const fetchOrder = useCallback(async () => {
    if (isLoading || !order_id || !appState?.customer?.id) {
      return;
    }
    dispatch(getbaseLineOrderAction(null));
    setIsLoading(true);
    setActiveBaseFlag('EngagementPage');

    try {
      const { orderForCustomer } = await makeCall(
        OrderApiService.fetchCurrentOrdersListManagement(appState.customer.id, {
          id: Number(order_id),
          size: 1,
        })
      );

      //  await getPurchaseOrder(order);

      if (orderForCustomer?.[0] && isMountedRef.current) {
        const order = orderForCustomer[0];
        await getPurchaseOrder(order);
        // dispatch(injectOrderAction(order));
        dispatch(getbaseLineSourceOrderAction(order));
        // await getPurchaseOrder(order);
        setSelectedScopes(order.tmscopes || []);
        setSelectedConsultants(order.tmconsultants || []);
        setSelectedDeliveryManager(order.tmaccountables || []);
        setSelectedRepresentative(order.tmdelegues || []);
        setAvailableScopes(order.scopes || []);
        setAvailableConsultants(order.consultants || []);
        setAvailableDeliveryManagers(order.delivery_managers || []);
        setAvailableRepresentatives(order.representatives || []);
        // dispatch(getbaseLineOrderAction(order));
        setSourceBaseOrder(order);
        if (order?.is_baselined) await savedFetchOrder(order?.is_baselined, order?.baseline_version);
        setIsLoading(false);
        console.log(' Order fetched and state updated successfully');
      } else {
        console.warn(' No order found or component unmounted');
        history.push(`/${appState.customer?.slug}/orders/production?status=production`);
      }
    } catch (error) {
      console.error(' Failed to fetch order:', error);
      setIsLoading(false);
      if (isMountedRef.current) {
        dispatch(
          setSnackbarAction({
            message: `Failed to fetch order: ${error}`,
            open: true,
            severity: 'error',
          })
        );
      }
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, []);

  // Initial fetch effect
  useEffect(() => {
    if (activeBaseFlag === 'EngagementPage' && currentStep === 1) {
      fetchOrder();
    }
    // else if (activeBaseFlag === 'EngagementPage' && sourceBaseOrder && currentStep === 1) {
    //   savedFetchOrder();
    // }
  }, [order_id, appState?.customer?.id, currentStep]); // Add proper dependencies

  // Cleanup on unmount
  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    []
  );

  // Debug effect to track sourceBaseOrder changes
  // useEffect(() => {
  //   console.log('🔄 sourceBaseOrder updated:', sourceBaseOrder);
  // }, [sourceBaseOrder]);

  // const bulkBCCreateOrderWorkunits = useCallback(async () => {
  //   console.log('bulk creation');
  //   if (selectedWorkunits.length) {
  //     await bulkCreateBaseLineOrderWorkunits(sourceBaseOrder?.id, baselineOrderData?.id);
  //   }
  // }, []);

  const handleSave = useCallback(
    async (fnName) => {
      if (currentStep === 1) {
        // Call child function from parent
        if (EngagementParametersRef.current) {
          EngagementParametersRef.current.handleSubmitEngagementPara(fnName);
          // handleNext();
        }
      } else if (currentStep === 2) {
        // handleNext();
        if (NewBaselineRef.current) {
          NewBaselineRef.current.handleSubmitNewbaseLine(fnName);
        }
      } else if (currentStep === 3) {
        handleNext();
      } else if (currentStep === 4) {
        // handleNext();
        dispatch(
          openDialogAction({
            name: 'changeBaselineConfirmDialog',
          })
        );
      }
    },
    [currentStep]
  );

  const handleStepClick = useCallback(
    (stepId: number) => {
      if (stepId === 1) {
        savedFetchOrder(true, Number(sourceOrder?.baseline_version));
      }
      if ((baseLineOrder?.baseLineOrder?.baseline_version ?? 0) > 0) {
        setActiveStep(steps.findIndex((step) => step.id === stepId));
        setCurrentStep(stepId);
        setCompleted((prevCompleted) => {
          const updatedCompleted = { ...prevCompleted };
          steps.forEach((step) => {
            if (step.id < stepId) {
              updatedCompleted[step.id] = true;
            } else {
              updatedCompleted[step.id] = false;
            }
          });
          return updatedCompleted;
        });
      } else {
        // Show popup: "Please save for first time"
        dispatch(
          setSnackbarAction({
            message: 'Please save for first time',
            open: true,
            severity: 'warning',
            duration: 1000,
          })
        );
      }

      // if (currentStep < 3 && currentStep > 0) handleSave('save');
    },
    [steps, dispatch, baselineOrderData?.id]
  );

  const handleNext = useCallback(() => {
    if (currentStep < totalSteps) {
      setIsCreating(true);
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      setActiveStep(steps.findIndex((step) => step.id === nextStep));
      setCompleted((prevCompleted) => {
        const updatedCompleted = { ...prevCompleted };
        steps.forEach((step) => {
          if (step.id < nextStep) {
            updatedCompleted[step.id] = true;
          } else {
            updatedCompleted[step.id] = false;
          }
        });
        setIsCreating(false);
        dispatch(injectSelectedWorkunitsAction([]));
        return updatedCompleted;
      });
    }
  }, [currentStep, totalSteps, steps]);

  const handleBack = useCallback(() => {
    if (currentStep > 1) {
      const nextStep = currentStep - 1;
      setCurrentStep(nextStep);
      if (nextStep === 1) {
        setActiveBaseFlag('EngagementPage');
        savedFetchOrder(true, Number(sourceOrder?.baseline_version));
      }
      setActiveStep(steps.findIndex((step) => step.id === nextStep));
      dispatch(injectSelectedWorkunitsAction([]));
      setCompleted((prevCompleted) => {
        const updatedCompleted = { ...prevCompleted };
        steps.forEach((step) => {
          if (step.id < nextStep) {
            updatedCompleted[step.id] = true;
          } else {
            updatedCompleted[step.id] = false;
          }
        });
        return updatedCompleted;
      });
    }
  }, [currentStep, steps]);

  const confirmCancelFn = async () => {
    if (sourceOrder && sourceOrder.is_baselined && currentStep !== 1) {
      const baselineVersion =
        sourceOrder?.baseline_version === 0 || sourceOrder?.baseline_version === null
          ? 1
          : Number(sourceOrder?.baseline_version) + 1;
      // const is_cancelled = 2;
      const data = await baselineCancelDialogApi(sourceOrder, 5, baselineVersion, currentStep);
      if (data) {
        dispatch(
          setSnackbarAction({
            message: `${sourceOrder?.id} Order baseline version not saved`,
            open: true,
            severity: 'success',
          })
        );
        history.goBack();
      } else {
        dispatch(
          setSnackbarAction({
            message: 'The order baseline version has not been deleted',
            open: true,
            severity: 'error',
          })
        );
      }
    } else {
      history.goBack();
    }
  };
  const handleCancel = useCallback(() => {
    history.goBack();
  }, [history]);

  const handleSaveAsDraft = () => {
    if (currentStep === 3 || currentStep === 4) handleCancel();
    else handleSave('saveAaDraft');
    // history.goBack();
  };

  // Validation
  useEffect(() => {
    if (baseLineOrder?.editOrder)
      setIsMissingInputs(
        moment(baseLineOrder?.editOrder?.end_date).isBefore(baseLineOrder?.editOrder?.start_date) ||
        !baseLineOrder?.editOrder?.end_date ||
        !baseLineOrder?.editOrder?.start_date ||
        selectedScopes.length === 0 ||
        selectedDeliveryManager.length === 0 ||
        selectedConsultants.length === 0 ||
        !baseLineOrder?.editOrder?.name_spec ||
        baseLineOrder?.editOrder?.name_spec?.length === 0
      );
  }, [baseLineOrder?.editOrder, selectedScopes, selectedDeliveryManager, selectedConsultants]);

  // Memoized step content
  const renderStepContent = useCallback(() => {
    switch (currentStep) {
      case 1:
        return (
          <EngagementParameters
            ref={EngagementParametersRef}
            selectedConsultants={selectedConsultants}
            setSelectedConsultants={setSelectedConsultants}
            deliveryManagers={deliveryManagers}
            setDeliveryManagers={setDeliveryManagers}
            selectedDeliveryManager={selectedDeliveryManager}
            setSelectedDeliveryManager={setSelectedDeliveryManager}
            representatives={representatives}
            setRepresentatives={setRepresentatives}
            selectedRepresentative={selectedRepresentative}
            setSelectedRepresentative={setSelectedRepresentative}
            selectedScopes={selectedScopes}
            setSelectedScopes={setSelectedScopes}
            isCreating={isCreating}
            setIsCreating={setIsCreating}
            currentStep={currentStep}
            setActiveBaseFlag={setActiveBaseFlag}
            handleNext={handleNext}
            handleCancel={handleCancel}
            savedFetchOrder={savedFetchOrder}
            availableScopes={availableScopes}
            availableDeliveryManagers={availableDeliveryManagers}
            availableConsultants={availableConsultants}
            customerId={appState?.customer?.id}
          />
        );
      case 2:
        return (
          <>
            {baseLineOrder && <NewBaseLineOrderWorkunits order={baseLineOrder} />}
            <NewBaseline
              ref={NewBaselineRef}
              currentStep={currentStep}
              handleNext={handleNext}
              handleCancel={handleCancel}
              setCurrentStep={setCurrentStep}
              setActiveBaseFlag={setActiveBaseFlag}
              setIsCreating={setIsCreating}
            />
          </>
        );
      case 3:
        return (
          <>
            {baseLineOrder && <NewBaseLineOrderWorkunits order={baseLineOrder} />}
            <BaselineAffectation />
          </>
        );
      case 4:
        return (
          <>
            {baseLineOrder && <NewBaseLineOrderWorkunits order={baseLineOrder} />}
            <BaselineOrderSummary
              currentStep={currentStep}
              handleNext={handleNext}
              handleCancel={handleCancel}
              history={history}
              location={location}
              match={match}
            />
          </>
        );
      default:
        return null;
    }
  }, [
    currentStep,
    selectedConsultants,
    deliveryManagers,
    availableDeliveryManagers,
    availableScopes,
    availableConsultants,
    selectedDeliveryManager,
    selectedRepresentative,
    representatives,
    selectedScopes,
    isCreating,
    baselineOrderData,
  ]);

  const buttonText = useMemo(() => {
    if (isCreating) return 'Saving...';
    if (currentStep === 4) return t('baseLine_done');
    return t('save_next');
  }, [isCreating, currentStep, t]);

  return (
    <Layout
      name={
        sourceBaseOrder?.id && sourceBaseOrder?.name_spec ? `${sourceBaseOrder.id}-${sourceBaseOrder.name_spec}` : ''
      }
      path="/changebaseline"
    >
      {currentStep !== 5 && (
        <>
          <StepperHeader
            steps={steps}
            activeStep={activeStep}
            disable={isLoading}
            completed={completed}
            onStepClick={handleStepClick}
          />
          {sourceBaseOrder?.id && !isLoading ? (
            <>
              {renderStepContent()}

              {/* The Box containing navigation buttons */}
              <Box
                sx={{
                  m: 'auto -30px -30px',
                  p: '30px',
                  backgroundColor: '#FFFFFF',
                  boxShadow: '0px 3px 16px #B2BCD571',
                }}
              >
                <Grid container spacing={4} alignItems="center" justifyContent="space-between">
                  <Grid item xs={12} md={4} lg={2}>
                    {(currentStep === 1 || currentStep === 2) && (
                      <Button
                        onClick={() => confirmCancelFn()}
                        disabled={false}
                        color="success"
                        variant="contained"
                      // startIcon={<ArrowBackIcon />}
                      >
                        {t('cancel')}
                      </Button>
                    )}
                  </Grid>

                  <Grid item xs={12} md={2} lg={2} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Grid item>
                      <Button
                        onClick={() => handleSaveAsDraft()}
                        disabled={isCreating || isLoading || isMissingInputs}
                        color="success"
                        variant="contained"
                        sx={{ width: 150, mr: 2 }}
                      >
                        {isCreating ? 'Saving...' : t('Save_as_draft_tm')}
                      </Button>
                    </Grid>

                    <Grid item>
                      <Button
                        onClick={() => handleBack()}
                        disabled={currentStep === 1 || isCreating || isLoading}
                        color="success"
                        variant="contained"
                        sx={{ width: 150, mr: 2 }}
                      >
                        {t('Back')}
                      </Button>
                    </Grid>

                    <Grid item>
                      <Button
                        onClick={() => handleSave('save')}
                        disabled={isCreating || isLoading || isMissingInputs}
                        color="success"
                        variant="contained"
                        sx={{ width: 150, mr: 2 }}
                      >
                        {buttonText}
                        {/* {isCreating ? 'Saving...' : currentStep === 4 ? t('baseLine_done') : t('save_next')} */}
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </Box>
            </>
          ) : (
            /* Loading state Box */
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
              Loading order data...
            </Box>
          )}
        </> /* End of the outer Fragment */
      )}
      {/* {currentStep === 5 && <EditTotalWorkunit scopes={Scopes} />} */}
      {dialogData?.changeBaselineConfirmDialog && <BaselineOrderConfirmation />}
      {dialogData?.changeBaselinetotalWorkunitDialog && <BaselineTotalWorkunit />}
      {dialogData?.changeBaselineeditworkunitDialog && <BaselineEditWorkunit />}
      {dialogData?.BaselineEditWorkunitCancel && <BaselineEditWorkunitCancelConfirmation />}
      {dialogData?.deliverableBaseLineDelete && <DeliverableBLDialogDelete />}
      {dialogData?.ConfirmationDialogQuickAction && <ConfirmationDialogQuickAction />}
    </Layout>
  );
};

export default React.memo(ChangeBaseline);
